import type { MonitoringInvestigationContext } from "../../../shared/chatInvestigation";
import { config, defaultManagedModel } from "../../environment";
import { styled } from "goober";
import { useMemo, useState } from "react";
import {
  MANAGED_AI_PROVIDER_ID,
  resolveAIProviderId,
} from "../../../shared/aiProviders";
import {
  isManagedAiConnectorId,
  MANAGED_AI_CONNECTOR_ID,
  MANAGED_AI_CONNECTOR_LABEL,
} from "../../../shared/aiProviders/managedAiConnector";
import type { MonitoringAiUsageResponse } from "../../../shared/aiUsageRecords";
import { queries } from "../../../shared/queries";
import { useManagedAIProvider } from "../../hooks/useManagedAIProvider";
import { useQuery } from "@rocicorp/zero/react";
import { useSpaceDisplayName } from "../../hooks/useSpaceDisplayName";
import { useRevisionedJson } from "../../hooks/useRevisionedJson";
import { monitoringAiUsagePath } from "../../lib/apiPaths";
import { navigate, routes, useCurrentUrl } from "../../lib/urls";
import { useMonitoringInteraction } from "./analytics";
import {
  SearchableDropdown,
  MIN_OPTIONS_FOR_SEARCH,
  RowButton,
  SectionLabel,
} from "../SearchableDropdown";
import { ConnectorLogo } from "../ConnectorLogo";
import { createdByName } from "../ConnectorRow";
import { EmptyState } from "../EmptyState";
import { Icon } from "../Icon";
import { ProviderLogo } from "../ProviderLogo";
import { Skeleton } from "../Skeleton";
import { Text } from "../Text";
import { AiBreakdownPanel } from "./AiBreakdownPanel";
import { dayFloor, windowOption, windowRange } from "./monitoringWindow";
import { MonitoringWindowPicker } from "./MonitoringWindowPicker";
import { AiOverviewStats } from "./AiOverviewStats";
import { AiSpendByModelPanel } from "./AiSpendByModelPanel";
import { AiSpendVsLimitsPanel } from "./AiSpendVsLimitsPanel";
import {
  aiRecordInScope,
  computeAiProviderMonitoringRows,
  computeModelCostRows,
  type ConnectorAiProvider,
  providerLabel,
  providerRowParts,
  spendCapsForConnectors,
} from "./aiProviderMonitoring";
import {
  buildUsagePriceLookup,
  managedAiDefaultPrices,
} from "../../lib/connectorUsage";
import { computeAiUsageSummary, withBaseModels } from "./aiUsage";
import { ControlButton } from "./ControlButton";
import { MonitoringLayout } from "./MonitoringLayout";
import { MonitoringScopeSelector } from "./MonitoringScopeSelector";
import { MonitoringSpacePicker } from "./MonitoringSpacePicker";
import { useMonitoringScope } from "./useMonitoringScope";
import { useMonitoringSpaces } from "./useMonitoringWorkflows";
import { MONITORING_PANEL_HEIGHT } from "./monitoringPanelLayout";
import { RefreshButton } from "./RefreshButton";
import { AiGrid, Content } from "./workflowsLayout";

const ALL_PROVIDERS = "__all__";
const EmptyCard = styled("div")`
  flex: 1;
  min-height: 0;
  padding: 32px;
  border-radius: 12px;
  background: var(--surface-secondary);
  border: 0.5px solid var(--border-alpha);
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ProviderTrigger = styled(ControlButton)`
  max-width: 280px;
`;

function LoadingPlaceholder() {
  return (
    <Content aria-hidden>
      <AiGrid>
        <Skeleton style={{ width: "100%", height: 90, borderRadius: 12 }} />
        <Skeleton
          style={{
            width: "100%",
            height: "100%",
            minHeight: 160,
            borderRadius: 12,
          }}
        />
        <Skeleton
          style={{
            width: "100%",
            height: "100%",
            minHeight: 160,
            borderRadius: 12,
          }}
        />
        <Skeleton
          style={{
            width: "100%",
            height: "100%",
            minHeight: MONITORING_PANEL_HEIGHT,
            borderRadius: 12,
          }}
        />
      </AiGrid>
    </Content>
  );
}

export function AiPanel() {
  const recordInteraction = useMonitoringInteraction();
  const [refreshToken, setRefreshToken] = useState(0);
  const [today0, setToday0] = useState(() => dayFloor(Date.now()));
  const refresh = () => {
    setToday0(dayFloor(Date.now()));
    setRefreshToken((token) => token + 1);
  };
  const [providers, providersQuery] = useQuery(queries.monitorAiProviders());
  const [connectors, connectorsQuery] = useQuery(queries.monitorAiConnectors());
  const [pricingRows, pricingQuery] = useQuery(queries.monitoringUsagePrices());
  const [budgets, budgetsQuery] = useQuery(queries.monitoringUsageBudgets());
  const { managedProvider } = useManagedAIProvider();
  const managed = useMemo(
    () =>
      managedProvider
        ? {
            label: MANAGED_AI_CONNECTOR_LABEL,
            chatModel: defaultManagedModel.chatModel,
            fastModel: defaultManagedModel.fastModel,
          }
        : null,
    [managedProvider]
  );
  // Managed AI has no price rows, so seed its sentinel from list-price estimates.
  const prices = useMemo(
    () =>
      buildUsagePriceLookup(
        pricingRows ?? [],
        managedAiDefaultPrices(config.awsRegion)
      ),
    [pricingRows]
  );

  const connectorById = useMemo(() => {
    const map = new Map<string, NonNullable<typeof connectors>[number]>();
    for (const connector of connectors ?? []) map.set(connector.id, connector);
    return map;
  }, [connectors]);

  const connectorLabels = useMemo(() => {
    const map = new Map<string, string>();
    for (const connector of connectors ?? [])
      map.set(connector.id, connector.name);
    return map;
  }, [connectors]);

  const connectorCreators = useMemo(() => {
    const map = new Map<string, string | null>();
    for (const connector of connectors ?? [])
      map.set(connector.id, createdByName(connector.createdBy));
    return map;
  }, [connectors]);

  const {
    spaces: attributionSpaces,
    spaceById,
    loading: spacesLoading,
  } = useMonitoringSpaces();
  const { window: windowParam, space: spaceParam } = useCurrentUrl(
    routes.monitoring.ai
  );
  const lookback = windowOption(windowParam, "30");
  const { start, end } = windowRange(lookback.value, today0);
  const { scope } = useMonitoringScope(routes.monitoring.ai);
  const spaceFilter = spaceParam
    ? (spaceById.get(spaceParam)?.id ?? null)
    : null;
  const spaceFilterPending = !!spaceParam && spacesLoading;
  // Read already collapsed rather than synced row by row, so the window only
  // moves on a refresh.
  const {
    data: aiUsage,
    loading: aiUsageLoading,
    refreshing: aiUsageRefreshing,
  } = useRevisionedJson<MonitoringAiUsageResponse>(
    monitoringAiUsagePath(),
    `only=usage&start=${start}&end=${end}`,
    refreshToken
  );
  // Limits are enforced over a rolling 30 days, so their panel ignores the picked window.
  const limitWindow = windowRange("30", today0);
  const {
    data: limitUsage,
    loading: limitLoading,
    refreshing: limitRefreshing,
  } = useRevisionedJson<MonitoringAiUsageResponse>(
    monitoringAiUsagePath(),
    `only=spend&start=${limitWindow.start}&end=${limitWindow.end}`,
    refreshToken
  );
  // The top 100 has to rank inside the picked space, so that filter goes to the
  // server, keyed off the synced spaces so the first request is already right.
  const {
    data: workflowUsage,
    loading: workflowLoading,
    refreshing: workflowRefreshing,
  } = useRevisionedJson<MonitoringAiUsageResponse>(
    monitoringAiUsagePath(),
    spaceFilterPending
      ? null
      : `only=workflows&start=${start}&end=${end}${spaceFilter ? `&space=${spaceFilter}` : ""}`,
    refreshToken
  );
  // The last payload is kept so a window change doesn’t blank the page mid-fetch.
  const [heldUsage, setHeldUsage] = useState(aiUsage);
  if (aiUsage && aiUsage !== heldUsage) setHeldUsage(aiUsage);
  const usage = aiUsage ?? heldUsage;
  const [heldLimitUsage, setHeldLimitUsage] = useState(limitUsage);
  if (limitUsage && limitUsage !== heldLimitUsage)
    setHeldLimitUsage(limitUsage);
  const limits = limitUsage ?? heldLimitUsage;
  // The header scope narrows both record sets before anything derives from them,
  // so every panel (overview, spend, models) reflects the same personal/team cut.
  const inScope = <R extends { spaceId: string | null; personal: boolean }>(
    records: readonly R[]
  ) =>
    records
      .map((record) => ({
        ...record,
        space: (record.spaceId ? spaceById.get(record.spaceId) : null) ?? null,
      }))
      .filter((record) => aiRecordInScope(scope, record));
  const scopedUsageRecords = inScope(withBaseModels(usage?.usageRecords ?? []));
  const spendRecords = inScope(limits?.spendRecords ?? []);

  const displayName = useSpaceDisplayName();
  const present = new Set(scopedUsageRecords.map((record) => record.spaceId));
  const spaceOptions = attributionSpaces
    .filter((space) => present.has(space.id) || space.id === spaceFilter)
    .map((space) => ({
      id: space.id,
      label: displayName(space),
      mode: space.mode,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));
  const usageRecords =
    spaceFilter == null
      ? scopedUsageRecords
      : scopedUsageRecords.filter((record) => record.spaceId === spaceFilter);
  const workflowRecords = inScope(
    withBaseModels(workflowUsage?.workflowRecords ?? [])
  );

  // AI usage that flowed through a connector with no configured ai_provider is
  // tagged with the connector id as its aiProviderId; surface each such
  // connector as its own provider row so its models are visible and selectable.
  const connectorProviderRows = useMemo<ConnectorAiProvider[]>(() => {
    const seen = new Set<string>();
    const result: ConnectorAiProvider[] = [];
    for (const record of usageRecords) {
      const connectorId = record.connectorId;
      if (!connectorId || isManagedAiConnectorId(connectorId)) continue;
      if (record.aiProviderId !== connectorId) continue;
      if (seen.has(connectorId)) continue;
      seen.add(connectorId);
      const connector = connectorById.get(connectorId);
      result.push({
        id: connectorId,
        label: connector?.name ?? "Connector",
        providerType: connector?.identifier ?? record.provider,
      });
    }
    return result;
  }, [usageRecords, connectorById]);

  const rows = useMemo(
    () =>
      computeAiProviderMonitoringRows(
        providers ?? [],
        usageRecords,
        prices,
        managed,
        connectorProviderRows,
        connectorLabels
      ),
    [
      providers,
      usageRecords,
      prices,
      managed,
      connectorProviderRows,
      connectorLabels,
    ]
  );

  const { provider: selectedId } = useCurrentUrl(routes.monitoring.ai);
  const setSelectedId = (id: string | null) => {
    recordInteraction("ai.provider_filter_change", {
      targetType: "provider",
      targetId: id ?? undefined,
    });
    navigate(routes.monitoring.ai, { provider: id ?? "" }, { replace: true });
  };
  const defaultId = rows.length > 1 ? ALL_PROVIDERS : (rows[0]?.id ?? null);
  const selectionValid =
    selectedId != null && rows.some((r) => r.id === selectedId);
  const effectiveId = selectionValid ? selectedId : defaultId;
  const isAll = effectiveId === ALL_PROVIDERS;

  const forProvider = <R extends { aiProviderId: string | null }>(
    records: readonly R[]
  ) =>
    isAll
      ? records
      : records.filter((record) =>
          effectiveId === MANAGED_AI_PROVIDER_ID
            ? record.aiProviderId == null
            : record.aiProviderId === effectiveId
        );
  const providerRecords = forProvider(usageRecords);
  const providerWorkflowRecords = forProvider(workflowRecords);
  const providerSpendRecords = forProvider(spendRecords);
  const summary = useMemo(
    () => computeAiUsageSummary(providerRecords, prices),
    [providerRecords, prices]
  );
  const modelRows = useMemo(
    () =>
      isAll
        ? computeModelCostRows(providerRecords, prices)
        : (rows.find((r) => r.id === effectiveId)?.modelRows ?? []),
    [isAll, providerRecords, prices, rows, effectiveId]
  );
  // AI spend is metered in the connector ledger, so a provider's caps live in
  // connector_usage_budgets keyed by its backing connector: the managed sentinel
  // for managed AI, a configured provider's connector, or a connector row's own
  // id. The all-providers view aggregates only the connectors surfaced as AI
  // providers here, so a budget on an idle or non-AI connector never leaks in.
  const capConnectorIds = useMemo(() => {
    if (!isAll) {
      if (effectiveId === MANAGED_AI_PROVIDER_ID)
        return [MANAGED_AI_CONNECTOR_ID];
      const provider = (providers ?? []).find((p) => p.id === effectiveId);
      const connectorId = provider
        ? provider.appUsage?.connectedAppId
        : effectiveId;
      return connectorId ? [connectorId] : [];
    }
    const ids = new Set<string>();
    for (const provider of providers ?? []) {
      if (provider.appUsage?.connectedAppId)
        ids.add(provider.appUsage.connectedAppId);
    }
    if (managed) ids.add(MANAGED_AI_CONNECTOR_ID);
    for (const connector of connectorProviderRows) ids.add(connector.id);
    return [...ids];
  }, [isAll, effectiveId, providers, managed, connectorProviderRows]);
  const caps = useMemo(
    () => spendCapsForConnectors(budgets ?? [], capConnectorIds),
    [budgets, capConnectorIds]
  );

  if (
    providersQuery.type !== "complete" ||
    connectorsQuery.type !== "complete" ||
    pricingQuery.type !== "complete" ||
    budgetsQuery.type !== "complete" ||
    (aiUsageLoading && !usage) ||
    (limitLoading && !limits)
  ) {
    return (
      <MonitoringLayout
        ready={false}
        headerItem={<MonitoringScopeSelector route={routes.monitoring.ai} />}
      >
        <LoadingPlaceholder />
      </MonitoringLayout>
    );
  }

  if (rows.length === 0) {
    return (
      <MonitoringLayout
        ready
        headerItem={<MonitoringScopeSelector route={routes.monitoring.ai} />}
      >
        <Content>
          <EmptyCard>
            <EmptyState
              iconName="brain"
              title="No AI providers"
              description="Add a provider in Settings → AI to start tracking cost and access."
              maxWidth={420}
            />
          </EmptyCard>
        </Content>
      </MonitoringLayout>
    );
  }

  const selectedRow = isAll
    ? null
    : (rows.find((r) => r.id === effectiveId) ?? null);
  const selectedProvider = isAll
    ? null
    : ((providers ?? []).find((p) => p.id === effectiveId) ?? null);
  const selectedConnector = isAll
    ? null
    : (connectorProviderRows.find((c) => c.id === effectiveId) ?? null);

  const selectedCheck = (
    <Icon name="check" size={12} color="var(--text-secondary)" />
  );
  const rowCheck = (
    <span style={{ flexShrink: 0, display: "flex", marginLeft: "auto" }}>
      {selectedCheck}
    </span>
  );

  const triggerLabel = isAll
    ? "All providers"
    : selectedProvider
      ? providerRowParts(selectedProvider, connectorLabels, connectorCreators)
          .name
      : selectedConnector
        ? selectedConnector.label
        : (selectedRow?.label ?? "");

  const providerPicker = (
    <SearchableDropdown
      searchable={rows.length >= MIN_OPTIONS_FOR_SEARCH}
      searchPlaceholder="Search providers…"
      opener={
        <ProviderTrigger as="span">
          {isAll ? (
            <Icon name="layers" size={12} />
          ) : effectiveId === MANAGED_AI_PROVIDER_ID && managedProvider ? (
            <Icon name="brain" size={12} />
          ) : selectedProvider ? (
            <ProviderLogo
              provider={resolveAIProviderId(selectedProvider)}
              size={12}
            />
          ) : selectedConnector ? (
            <ConnectorLogo
              template={{
                name: selectedConnector.label,
                identifier: selectedConnector.providerType,
              }}
              size={12}
            />
          ) : null}
          <Text variant="text-small-md" ellipsis>
            {triggerLabel}
          </Text>
          <Icon name="chevron-down" size={12} color="var(--text-secondary)" />
        </ProviderTrigger>
      }
    >
      {(query, close) => {
        const q = query.trim().toLowerCase();
        const managedMatches =
          managedProvider != null &&
          (!q || MANAGED_AI_CONNECTOR_LABEL.toLowerCase().includes(q));
        const matchedProviders = (providers ?? []).filter(
          (provider) =>
            !q ||
            providerLabel(provider, connectorLabels).toLowerCase().includes(q)
        );
        const matchedConnectors = connectorProviderRows.filter(
          (connector) => !q || connector.label.toLowerCase().includes(q)
        );
        const hasMatches =
          managedMatches ||
          matchedProviders.length > 0 ||
          matchedConnectors.length > 0;
        return (
          <>
            {rows.length > 1 && !q && (
              <RowButton
                onClick={() => {
                  setSelectedId(null);
                  close();
                }}
              >
                <Icon name="layers" size={16} color="var(--text-primary)" />
                <div style={{ flex: 1, minWidth: 0, textAlign: "left" }}>
                  <Text variant="text-regular" ellipsis>
                    All providers
                  </Text>
                  <Text
                    variant="text-small"
                    color="var(--text-secondary)"
                    ellipsis
                  >
                    {rows.length} providers
                  </Text>
                </div>
                {isAll && rowCheck}
              </RowButton>
            )}
            {managedMatches && (
              <RowButton
                onClick={() => {
                  setSelectedId(MANAGED_AI_PROVIDER_ID);
                  close();
                }}
              >
                <Icon name="brain" size={16} color="var(--text-primary)" />
                <div style={{ flex: 1, minWidth: 0, textAlign: "left" }}>
                  <Text variant="text-regular" ellipsis>
                    {MANAGED_AI_CONNECTOR_LABEL}
                  </Text>
                  <Text
                    variant="text-small"
                    color="var(--text-secondary)"
                    ellipsis
                  >
                    Anthropic and OpenAI
                  </Text>
                </div>
                {effectiveId === MANAGED_AI_PROVIDER_ID && rowCheck}
              </RowButton>
            )}
            {matchedProviders.map((provider) => {
              const { name, secondary } = providerRowParts(
                provider,
                connectorLabels,
                connectorCreators
              );
              return (
                <RowButton
                  key={provider.id}
                  onClick={() => {
                    setSelectedId(provider.id);
                    close();
                  }}
                >
                  <ProviderLogo
                    provider={resolveAIProviderId(provider)}
                    size={16}
                  />
                  <div style={{ flex: 1, minWidth: 0, textAlign: "left" }}>
                    <Text variant="text-regular" ellipsis>
                      {name}
                    </Text>
                    {secondary && (
                      <Text
                        variant="text-small"
                        color="var(--text-secondary)"
                        ellipsis
                      >
                        {secondary}
                      </Text>
                    )}
                  </div>
                  {provider.id === effectiveId && rowCheck}
                </RowButton>
              );
            })}
            {matchedConnectors.map((connector) => {
              const secondary = connectorCreators.get(connector.id) ?? null;
              return (
                <RowButton
                  key={connector.id}
                  onClick={() => {
                    setSelectedId(connector.id);
                    close();
                  }}
                >
                  <ConnectorLogo
                    template={{
                      name: connector.label,
                      identifier: connector.providerType,
                    }}
                    size={16}
                  />
                  <div style={{ flex: 1, minWidth: 0, textAlign: "left" }}>
                    <Text variant="text-regular" ellipsis>
                      {connector.label}
                    </Text>
                    {secondary && (
                      <Text
                        variant="text-small"
                        color="var(--text-secondary)"
                        ellipsis
                      >
                        {secondary}
                      </Text>
                    )}
                  </div>
                  {connector.id === effectiveId && rowCheck}
                </RowButton>
              );
            })}
            {!hasMatches && q !== "" && (
              <SectionLabel>
                <Text variant="text-small" color="var(--text-secondary)">
                  No providers found.
                </Text>
              </SectionLabel>
            )}
          </>
        );
      }}
    </SearchableDropdown>
  );

  const investigation: MonitoringInvestigationContext = {
    period: lookback.label,
    timeRange: { start, end: end + 86_400_000 },
    filters: {
      spaceId: spaceFilter,
      providerId: isAll ? null : effectiveId,
      connectorIds: [...capConnectorIds],
    },
  };

  return (
    <MonitoringLayout
      ready
      headerItem={<MonitoringScopeSelector route={routes.monitoring.ai} />}
    >
      <Content>
        <AiGrid
          style={{ opacity: aiUsage ? 1 : 0.5 }}
          aria-busy={!aiUsage || undefined}
        >
          <AiOverviewStats
            summary={summary}
            picker={
              <>
                {providerPicker}
                {spaceOptions.length > 1 && (
                  <MonitoringSpacePicker
                    options={spaceOptions}
                    value={spaceFilter}
                    onChange={(id) => {
                      recordInteraction("ai.space_filter_change", {
                        targetType: "space",
                        targetId: id ?? undefined,
                      });
                      navigate(
                        routes.monitoring.ai,
                        { space: id ?? "" },
                        { replace: true }
                      );
                    }}
                  />
                )}
                <MonitoringWindowPicker
                  value={lookback}
                  onChange={(option) => {
                    recordInteraction("ai.window_change", {
                      value: option.value,
                    });
                    navigate(
                      routes.monitoring.ai,
                      { window: option.value },
                      { replace: true }
                    );
                  }}
                />
                <RefreshButton
                  spinning={
                    aiUsageRefreshing || workflowRefreshing || limitRefreshing
                  }
                  onClick={refresh}
                />
              </>
            }
          />
          <AiSpendVsLimitsPanel
            loading={limitLoading || !limitUsage}
            investigation={{
              ...investigation,
              period: "Rolling 30 days",
              timeRange: {
                start: limitWindow.start,
                end: limitWindow.end + 86_400_000,
              },
              filters: { ...investigation.filters, spaceId: null },
            }}
            providerId={isAll ? null : effectiveId}
            limitAmount={caps.total}
            spaceBudgets={caps.spaces}
            spendRecords={providerSpendRecords}
          />
          <AiSpendByModelPanel
            loading={aiUsageLoading || !aiUsage || spaceFilterPending}
            modelRows={modelRows}
            lookback={lookback}
            investigation={investigation}
          />
          <AiBreakdownPanel
            loading={aiUsageLoading || !aiUsage || spaceFilterPending}
            investigation={investigation}
            lookback={lookback}
            records={providerRecords}
            workflowRecords={providerWorkflowRecords}
            workflowsLoading={
              workflowLoading || !workflowUsage || spaceFilterPending
            }
            prices={prices}
            connectorLabels={connectorLabels}
          />
        </AiGrid>
      </Content>
    </MonitoringLayout>
  );
}
